// @ts-nocheck

import React, { useEffect, useState, useRef } from "react";
import { Code, Zap, Database, Lock, GitBranch, Server, Cloud, Monitor, Settings, Coffee, Layers } from "lucide-react";

const EnhancedDeveloperCard = () => {
  const [animate, setAnimate] = useState(false);
  const containerRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [pulseState, setPulseState] = useState(0);
  
  useEffect(() => {
    // Trigger animation after component mounts
    setAnimate(true);
    
    // Make the main component animation loop
    const interval = setInterval(() => {
      setAnimate(false);
      setTimeout(() => setAnimate(true), 100);
    }, 25000);
    
    // Continuous animation for main block
    const pulseInterval = setInterval(() => {
      setPulseState(prev => (prev + 1) % 4);
    }, 2000);
    
    // Add mouse move tracking for parallax effect
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMousePosition({
          x: (e.clientX - rect.left) / rect.width - 0.5,
          y: (e.clientY - rect.top) / rect.height - 0.5
        });
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      clearInterval(interval);
      clearInterval(pulseInterval);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Syntax highlighted code
  const codeSnippet = `import React from "react";
import { useState, useEffect } from "react";

const App = () => {
  const [data, setData] = useState([]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("/api/data");
        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    
    fetchData();
  }, []);
  
  return (
    <div className="app">
      <h1>Welcome to our Platform</h1>
      {data.map(item => (
        <div key={item.id}>{item.name}</div>
      ))}
    </div>
  );
};

export default App;`;

  // Generate particles for animated background
  const particles = Array(25).fill().map((_, i) => ({
    size: Math.floor(Math.random() * 8) + 2,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
    duration: Math.floor(Math.random() * 15) + 10,
    delay: Math.random() * 5
  }));
  
  // Icons that will animate in sequence
  const icons = [
    { Icon: GitBranch, color: "text-green-500", delay: 100 },
    { Icon: Zap, color: "text-yellow-500", delay: 300 },
    { Icon: Database, color: "text-blue-500", delay: 500 },
    { Icon: Lock, color: "text-purple-500", delay: 700 },
    { Icon: Server, color: "text-red-500", delay: 900 },
    { Icon: Cloud, color: "text-blue-400", delay: 1100 },
    { Icon: Monitor, color: "text-indigo-500", delay: 1300 }
  ];
  
  // Generate floating tech elements
  const floatingElements = [
    { type: "square", color: "bg-blue-400", size: "w-16 h-16", top: "top-1/5", left: "left-1/5", rotate: "rotate-12", anim: "animate-pulse", delay: 0, opacity: 0.3 },
    { type: "circle", color: "bg-indigo-400", size: "w-12 h-12", top: "top-1/3", right: "right-1/4", rotate: "-rotate-12", anim: "animate-bounce", delay: 300, opacity: 0.35 },
    { type: "square", color: "bg-purple-400", size: "w-14 h-14", bottom: "bottom-1/3", left: "left-1/3", rotate: "rotate-45", anim: "animate-pulse", delay: 500, opacity: 0.35 },
    { type: "circle", color: "bg-green-400", size: "w-10 h-10", top: "top-2/3", right: "right-1/5", rotate: "rotate-90", anim: "animate-bounce", delay: 700, opacity: 0.4 },
    { type: "square", color: "bg-yellow-400", size: "w-20 h-20", bottom: "bottom-1/4", right: "right-1/3", rotate: "-rotate-12", anim: "animate-pulse", delay: 200, opacity: 0.3 },
    { type: "triangle", color: "border-red-400", size: "w-16 h-16", top: "top-1/6", right: "right-1/6", rotate: "rotate-45", anim: "animate-pulse", delay: 600, opacity: 0.35 },
    { type: "diamond", color: "bg-pink-400", size: "w-12 h-12", bottom: "bottom-1/6", left: "left-1/6", rotate: "rotate-45", anim: "animate-float", delay: 800, opacity: 0.35 },
    { type: "hexagon", color: "border-cyan-400", size: "w-16 h-14", top: "top-1/2", left: "left-1/6", rotate: "rotate-12", anim: "animate-pulse", delay: 400, opacity: 0.4 }
  ];

  // Generate more connecting lines
  const connectingLines = [
    { x1: "30%", y1: "30%", x2: "70%", y2: "50%", color: "stroke-blue-400", dasharray: 200, delay: 0 },
    { x1: "20%", y1: "60%", x2: "60%", y2: "30%", color: "stroke-purple-400", dasharray: 240, delay: 500 },
    { x1: "65%", y1: "70%", x2: "35%", y2: "40%", color: "stroke-green-400", dasharray: 180, delay: 1000 },
    { x1: "10%", y1: "40%", x2: "50%", y2: "80%", color: "stroke-red-400", dasharray: 220, delay: 1500 },
    { x1: "80%", y1: "20%", x2: "20%", y2: "20%", color: "stroke-yellow-400", dasharray: 260, delay: 2000 },
    { x1: "75%", y1: "40%", x2: "85%", y2: "75%", color: "stroke-indigo-400", dasharray: 160, delay: 2500 }
  ];

  return (
    <div className="w-full md:w-2/3 lg:w-1/2 mx-auto" ref={containerRef}>
      <div className="relative p-6">
        {/* Main card with white background - continuously animated */}
        <div 
          className={`relative z-10 bg-white rounded-xl shadow-xl overflow-hidden border border-gray-100 transition-all duration-1000 ${
            animate ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          } animate-subtle-float`}
          style={{
            transform: `perspective(1000px) rotateX(${mousePosition.y * 5}deg) rotateY(${mousePosition.x * 5}deg) 
                        ${pulseState === 0 ? 'scale(1.01)' : pulseState === 2 ? 'scale(0.99)' : 'scale(1)'}`,
            transition: 'transform 0.8s ease-out',
            boxShadow: `0 10px 25px -5px rgba(${66 + pulseState * 10}, ${84 + pulseState * 10}, ${255 - pulseState * 10}, 0.${25 + pulseState * 2})`
          }}
        >
          <div className="h-96 bg-white relative overflow-hidden">
            {/* Subtle grid pattern overlay */}
            <div 
              className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white" 
              style={{ 
                backgroundImage: "radial-gradient(#e0e7ff 1px, transparent 1px)", 
                backgroundSize: "20px 20px" 
              }}
            ></div>
            
            {/* Animated particles with increased count and opacity */}
            {particles.map((particle, i) => (
              <div 
                key={i}
                className={`absolute rounded-full bg-blue-400 ${animate ? "animate-float" : ""}`}
                style={{
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  left: particle.left,
                  top: particle.top,
                  animationDuration: `${particle.duration}s`,
                  animationDelay: `${particle.delay}s`,
                  transform: `translateY(${animate ? '0' : '20px'})`,
                  opacity: animate ? 0.45 : 0
                }}
              ></div>
            ))}
            
            {/* Code structure animation with typing effect - BLACK color with higher opacity */}
            <div className="absolute inset-0 text-black overflow-hidden">
              <pre className={`font-mono text-xs sm:text-sm md:text-base leading-relaxed p-6 transition-all duration-1000 ${
                animate ? "opacity-100" : "opacity-0"
              }`}>
                {codeSnippet}
              </pre>
            </div>
            
            {/* More animated floating elements with higher opacity and special shapes */}
            {floatingElements.map((element, index) => (
              <div 
                key={index}
                className={`absolute ${element.color} ${element.size} ${element.top || ''} ${element.left || ''} ${element.right || ''} ${element.bottom || ''} 
                  transition-all duration-1000 delay-${element.delay} ${
                  animate ? `opacity-${element.opacity * 100} transform ${element.rotate} scale-100 ${element.anim}` : "opacity-0 transform rotate-0 scale-50"
                }`}
                style={{
                  opacity: animate ? element.opacity : 0,
                  transitionDelay: `${element.delay}ms`,
                  clipPath: element.type === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : 
                            element.type === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                            element.type === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' : 
                            undefined
                }}
              ></div>
            ))}
            
            {/* Animated connecting lines with more complexity */}
            <svg className="absolute inset-0 w-full h-full z-0 opacity-30">
              {connectingLines.map((line, index) => (
                <line 
                  key={index}
                  x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} 
                  className={`${line.color} ${animate ? "animate-draw" : ""}`}
                  strokeWidth="1.5" 
                  strokeDasharray={line.dasharray}
                  strokeDashoffset={animate ? "0" : line.dasharray}
                  style={{ transition: `stroke-dashoffset 2s ease ${line.delay}ms` }}
                />
              ))}
            </svg>
            
            {/* Background grid patterns */}
            <div className="absolute inset-0 opacity-5">
              <div className="grid grid-cols-8 grid-rows-8 h-full w-full">
                {Array(64).fill().map((_, i) => (
                  <div key={i} className="border border-indigo-200"></div>
                ))}
              </div>
            </div>
            
            {/* Tech icons that animate in sequence with more icons */}
            <div className="absolute top-0 left-0 w-full pt-6 px-8 flex justify-between">
              {icons.map(({Icon, color, delay}, index) => (
                <div 
                  key={index}
                  className={`${color} transition-all duration-700`}
                  style={{
                    opacity: animate ? 1 : 0,
                    transform: animate ? 'translateY(0)' : 'translateY(-20px)',
                    transitionDelay: `${delay}ms`
                  }}
                >
                  <Icon size={18} strokeWidth={1.5} className="animate-subtle-pulse" />
                </div>
              ))}
            </div>
            
            {/* Second row of tech icons */}
            <div className="absolute bottom-24 left-0 w-full px-16 flex justify-around">
              {[
                { Icon: Settings, color: "text-gray-400", delay: 1200 },
                { Icon: Coffee, color: "text-yellow-700", delay: 1500 },
                { Icon: Layers, color: "text-orange-500", delay: 1800 }
              ].map(({Icon, color, delay}, index) => (
                <div 
                  key={index}
                  className={`${color} transition-all duration-700`}
                  style={{
                    opacity: animate ? 0.8 : 0,
                    transform: animate ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: `${delay}ms`
                  }}
                >
                  <Icon size={24} strokeWidth={1.5} className="animate-subtle-float" />
                </div>
              ))}
            </div>
            
            {/* Central logo with enhanced animations */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className={`relative transition-all duration-1000 ${
                animate ? "transform scale-100" : "transform scale-0"
              }`}>
                {/* Multiple rotating rings around central icon */}
                <div className="absolute w-48 h-48 rounded-full border border-dashed border-blue-200 animate-spin-slow opacity-70"></div>
                <div className="absolute w-44 h-44 rounded-full border border-dotted border-indigo-200 animate-reverse-spin opacity-70"></div>
                <div className="absolute w-40 h-40 rounded-full border-2 border-dashed border-blue-300 animate-spin-slow opacity-70"></div>
                <div className="absolute w-36 h-36 rounded-full border-2 border-dotted border-indigo-300 animate-reverse-spin opacity-70"></div>
                
                {/* Pulsing halo */}
                <div className="absolute w-32 h-32 rounded-full bg-blue-50 opacity-50 animate-pulse"></div>
                
                {/* Main central element */}
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 border-4 border-white shadow-lg flex items-center justify-center z-10">
                  <div className="absolute inset-0 rounded-full border-t-4 border-blue-500 animate-spin"></div>
                  <div className="absolute inset-0 rounded-full border-l-4 border-indigo-500 animate-reverse-spin"></div>
                  <div className="absolute inset-0 rounded-full border-r-2 border-purple-400 animate-spin-slow opacity-70"></div>
                  <div className="relative z-10">
                    <Code className="w-10 h-10 text-indigo-600" />
                  </div>
                </div>
                
                {/* Multiple decorative badges */}
                <div className={`absolute -bottom-2 -right-2 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg transition-all duration-1000 delay-700 ${
                  animate ? "transform scale-100 rotate-0 animate-pulse" : "transform scale-0 rotate-90"
                }`}>
                  +
                </div>
                
                <div className={`absolute -top-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg transition-all duration-1000 delay-900 ${
                  animate ? "transform scale-100 rotate-0 animate-float" : "transform scale-0 rotate-90"
                }`}>
                  <Zap size={14} />
                </div>
                
                <div className={`absolute -bottom-1 -left-1 w-7 h-7 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg transition-all duration-1000 delay-1100 ${
                  animate ? "transform scale-100 rotate-0 animate-bounce" : "transform scale-0 rotate-90"
                }`}>
                  <Database size={12} />
                </div>
              </div>
            </div>
          </div>
          
          {/* Text content with typing animation and pulsing effect */}
          <div className={`px-8 py-5 bg-white border-t border-gray-100 transition-all duration-1000 delay-300 ${
            animate ? "transform translate-y-0 opacity-100" : "transform translate-y-full opacity-0"
          }`}>
            <h3 className="text-lg font-semibold text-gray-800 flex items-center mb-2">
              <span className="relative">
                Transformation with technology
                <span 
                  className={`absolute -bottom-0.5 left-0 bg-blue-400 h-0.5 transition-all duration-1000 delay-1000 ${
                    animate ? "w-full" : "w-0"
                  }`}
                ></span>
              </span>
            </h3>
            <p className="text-gray-600">Clean, efficient and modern practices for rapid advancement.</p>
          </div>
        </div>
        
        {/* Enhanced background decorative elements with animation */}
        <div className={`absolute -bottom-12 -right-12 w-72 h-72 bg-blue-50 rounded-full transition-all duration-1000 delay-200 animate-subtle-pulse ${
          animate ? "opacity-50 transform scale-100" : "opacity-0 transform scale-75"
        }`}></div>
        <div className={`absolute -top-12 -left-12 w-64 h-64 bg-indigo-50 rounded-full transition-all duration-1000 delay-400 animate-subtle-float ${
          animate ? "opacity-50 transform scale-100" : "opacity-0 transform scale-75"
        }`}></div>
        <div className={`absolute top-1/4 -right-8 w-32 h-32 bg-purple-50 rounded-full transition-all duration-1000 delay-600 animate-subtle-pulse ${
          animate ? "opacity-50 transform scale-100" : "opacity-0 transform scale-75"
        }`}></div>
        <div className={`absolute bottom-1/4 -left-8 w-40 h-40 bg-green-50 rounded-full transition-all duration-1000 delay-800 animate-subtle-float ${
          animate ? "opacity-50 transform scale-100" : "opacity-0 transform scale-75"
        }`}></div>
        <div className={`absolute top-2/3 right-1/3 w-24 h-24 bg-yellow-50 rounded-full transition-all duration-1000 delay-1000 animate-subtle-pulse ${
          animate ? "opacity-40 transform scale-100" : "opacity-0 transform scale-75"
        }`}></div>
      </div>
    </div>
  );
};

export default EnhancedDeveloperCard;

// Add these animations to your CSS or as inline styles
const style = document.createElement('style');
style.textContent = `
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-15px); }
  }
  
  @keyframes draw {
    from { stroke-dashoffset: 200; }
    to { stroke-dashoffset: 0; }
  }
  
  .animate-float {
    animation: float 3s ease-in-out infinite;
  }
  
  .animate-draw {
    animation: draw 2s ease forwards;
  }
  
  .animate-spin-slow {
    animation: spin 20s linear infinite;
  }
  
  .animate-reverse-spin {
    animation: spin 15s linear infinite reverse;
  }
`;
document.head.appendChild(style);